import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Models = () => {
  const [boatModels, setBoatModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBoats();
  }, []);

  const fetchBoats = async () => {
    try {
      const response = await axios.get(`${API}/boats`);
      setBoatModels(response.data);
    } catch (err) {
      console.error('Failed to fetch boats', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="models-page">
      {/* Header */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
            Our Fleet
          </p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Boat Models
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            From river runners to landing craft, we build commercial-grade aluminum boats 
            for every Alaskan adventure.
          </p>
        </div>
      </section>

      {/* Models List */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {boatModels.map((model, index) => (
              <div 
                key={model.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`model-${model.id}`}
              >
                {/* Images */}
                <div className={`space-y-4 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="aspect-video overflow-hidden bg-slate-200">
                    <img 
                      src={model.images[0]} 
                      alt={model.name}
                      className="w-full h-full object-cover img-hover"
                    />
                  </div>
                  {model.images.length > 1 && (
                    <div className="grid grid-cols-2 gap-4">
                      {model.images.slice(1, 3).map((img, i) => (
                        <div key={i} className="aspect-video overflow-hidden bg-slate-200">
                          <img 
                            src={img} 
                            alt={`${model.name} ${i + 2}`}
                            className="w-full h-full object-cover img-hover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                    {model.name}
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {model.description}
                  </p>

                  {/* Prices */}
                  <div className="bg-white border-2 border-slate-100 p-6 mb-6">
                    <h4 className="font-heading text-sm uppercase tracking-widest text-slate-500 mb-4">
                      Pricing
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {model.prices.map((item, i) => (
                        <div key={i} className="text-center p-3 bg-slate-50">
                          <span className="block text-sm text-slate-500 mb-1">{item.size}</span>
                          <span className="font-heading text-xl font-bold text-orange-500">
                            {item.price}
                          </span>
                        </div>
                      ))}
                    </div>
                    {model.sizes && (
                      <p className="text-sm text-slate-500 mt-4">
                        <strong>Available sizes:</strong> {model.sizes}
                      </p>
                    )}
                  </div>

                  {/* Features if present */}
                  {model.features && model.features.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-heading text-sm uppercase tracking-widest text-slate-500 mb-3">
                        Key Features
                      </h4>
                      <ul className="space-y-2">
                        {model.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-slate-600">
                            <span className="w-2 h-2 bg-orange-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* External Link if present */}
                  {model.externalLink && (
                    <a 
                      href={model.externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-orange-500 font-semibold hover:text-orange-600 mb-6"
                    >
                      View Specmar Designs
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}

                  <Link 
                    to="/contact" 
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid={`model-cta-${model.id}`}
                  >
                    Start a Project
                    <ChevronRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Don't See What You Need?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
            We're a custom build shop. Tell us your vision and we'll make it happen.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
            Contact Us
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Models;
